import * as React from 'react'
import { toast } from 'sonner'

import { AccessLock } from '@/components/AccessLock'
import { Badge } from '@/components/ui/badge'
import { CopyButton } from '@/components/ui/copy-button'
import {
  notGradedReasonText,
  rubricReport,
} from '@/features/calls/rubricReport'
import type {
  AgentPromptUse,
  NotGraded,
  ParsedRubricReport,
  RubricResult,
} from '@/features/calls/rubricReport'
import { CareforceOnlyBadge } from '@/features/evaluation-rubrics/CareforceOnlyBadge'
import type { RubricType } from '@/hooks/useConfigAdmin'
import {
  AGENT_PROMPT_BADGE_CLASS,
  RUBRIC_TYPE_ORDER,
  rubricTypeLabel,
} from '@/features/evaluation-rubrics/rubricTypes'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { RubricDetail, RubricHeadline } from './RubricResult'
import { rubricView } from '@/features/calls/rubricView'

/**
 * Just enough of a call to render its saved report. Both `CallRecord` (the
 * Calls sheet) and `CallDetail` (the detail page Analysis tab) satisfy it, so
 * the two surfaces share this panel rather than drifting apart.
 */
export type RubricAnalysisCall = {
  eval_report_json?: unknown | null
}

/**
 * LLM-judge rubric verdicts for one call, read-only. The saved
 * report renders inline (there is no modal), so an already-evaluated call shows
 * its verdicts as soon as the surface opens. Access is gated by the caller
 * (`useRubricAnalysisAccess`).
 */
export function RubricAnalysisPanel({
  call,
  staffOnly = false,
}: {
  call: RubricAnalysisCall
  /** Shown only because the viewer is Careforce staff — draws the lock. */
  staffOnly?: boolean
}) {
  const { parsedReport, notGraded, reportJsonText, hasReport } = React.useMemo(
    () => rubricReport(call.eval_report_json),
    [call.eval_report_json]
  )

  return (
    <>
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium tracking-wide">
          Evaluation Rubric Results
          {staffOnly && <AccessLock className="h-3.5 w-3.5" />}
        </p>
        {hasReport && (
          <CopyButton
            content={reportJsonText}
            label="Copy report JSON"
            onCopy={() => toast.success('Report JSON copied to clipboard')}
            onError={() => toast.error('Failed to copy report')}
            className="hover:text-primary-high-contrast shrink-0 [&_svg]:size-3.5"
          />
        )}
      </div>

      {notGraded ? (
        <NotGradedNotice notGraded={notGraded} />
      ) : parsedReport ? (
        <Verdicts parsed={parsedReport} />
      ) : hasReport ? (
        // Report on file that doesn't match the pydantic-evals shape we parse —
        // show the raw JSON rather than pretending there is nothing here.
        <pre className="bg-neutral-subtle-bg max-h-[40vh] overflow-auto rounded-md border p-3 font-mono text-xs break-words whitespace-pre-wrap">
          {reportJsonText}
        </pre>
      ) : (
        <div className="bg-neutral-subtle-bg text-muted-foreground rounded-md px-3 py-6 text-center text-sm">
          No rubric report for this call.
        </div>
      )}
    </>
  )
}

/** The judge skipped the call: it had no conversation to grade, so it counts
 *  toward no rubric's pass rate. */
function NotGradedNotice({ notGraded }: { notGraded: NotGraded }) {
  return (
    <div className="bg-neutral-subtle-bg space-y-1 rounded-md px-3 py-3">
      <div className="flex items-center gap-2">
        <Badge variant="warning">Not graded</Badge>
        <span className="text-sm font-medium">No conversation</span>
      </div>
      <p className="text-muted-foreground text-xs">
        {notGradedReasonText(notGraded)} Rubrics aren’t graded on calls like
        this, and they don’t count toward pass rates.
      </p>
    </div>
  )
}

/** Known types in editor order; a type this UI doesn't know sorts last. */
function typeRank(rubric: RubricResult): number {
  const rank = RUBRIC_TYPE_ORDER.indexOf(rubric.type as RubricType)
  return rank === -1 ? RUBRIC_TYPE_ORDER.length : rank
}

function Verdicts({ parsed }: { parsed: ParsedRubricReport }) {
  // Failures first, so the list leads with what went wrong. Then by type,
  // then by title.
  const rubrics = React.useMemo(
    () =>
      parsed.rubrics
        .toSorted((a, b) => {
          const failedA = a.type === 'pass_fail' && !a.passed
          const failedB = b.type === 'pass_fail' && !b.passed
          if (failedA !== failedB) {
            return failedA ? -1 : 1
          }
          if (a.type !== b.type) {
            return typeRank(a) - typeRank(b)
          }
          const byName = a.title.localeCompare(b.title)
          return byName !== 0 ? byName : a.key.localeCompare(b.key)
        })
        .map((rubric) => ({ rubric, view: rubricView(rubric) })),
    [parsed.rubrics]
  )

  const ungraded = parsed.ungraded

  return (
    <div className="space-y-2.5">
      {rubrics.length === 0 && ungraded.length === 0 ? (
        <div className="bg-neutral-subtle-bg text-muted-foreground rounded-md px-3 py-6 text-center text-sm">
          No criteria in this report.
        </div>
      ) : (
        // No max-height on purpose: the page (or sheet) is the scroll
        // container, and a second scrollbar inside it strands the tail of a
        // long rubric behind a nested gutter.
        <TooltipProvider>
          <ul className="divide-y rounded-md border">
            {rubrics.map(({ rubric, view }) => (
              <li key={rubric.key} className="space-y-1.5 px-3 py-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                    <span className="text-sm leading-snug font-medium">
                      {rubric.title}
                    </span>
                    <Badge
                      variant="outline"
                      className="text-muted-foreground px-1.5 py-0 text-[10px]">
                      {rubricTypeLabel(
                        rubric.type === 'unsupported'
                          ? rubric.rubricType
                          : rubric.type
                      )}
                    </Badge>
                    <AgentPromptBadge use={rubric.agentPrompt} />
                    {rubric.careforceOnly && <CareforceOnlyBadge />}
                  </div>
                  <RubricHeadline view={view} />
                </div>
                <RubricDetail view={view} />
              </li>
            ))}
            {ungraded.map((rubric) => (
              <li key={rubric.key} className="space-y-1.5 px-3 py-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                    <span className="text-sm leading-snug font-medium">
                      {rubric.title}
                    </span>
                    <Badge
                      variant="outline"
                      className="text-muted-foreground px-1.5 py-0 text-[10px]">
                      {rubricTypeLabel(rubric.rubricType)}
                    </Badge>
                    {rubric.careforceOnly && <CareforceOnlyBadge />}
                  </div>
                  <Badge variant="warning">Not graded</Badge>
                </div>
                {rubric.error && (
                  <p className="text-muted-foreground text-xs break-words">
                    {rubric.error}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </TooltipProvider>
      )}
    </div>
  )
}

/**
 * Marks a rubric that asked to see the agent's system prompt: whether the
 * judge had it, or graded the transcript alone because the call stored none
 * or was handed off between duties.
 */
function AgentPromptBadge({ use }: { use: AgentPromptUse }) {
  if (use === null) {
    return null
  }
  const used = use === 'used'
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          tabIndex={0}
          className={cn(
            'px-1.5 py-0 text-[10px]',
            used
              ? AGENT_PROMPT_BADGE_CLASS
              : 'text-muted-foreground border-dashed'
          )}>
          {used ? 'With agent instructions' : 'Transcript only'}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        {used
          ? 'The judge also read the system prompt the agent was given on this call.'
          : 'This rubric asks for the agent’s instructions, but this call has no single set to use: none were stored, or the call was handed off between duties. The judge read the transcript alone.'}
      </TooltipContent>
    </Tooltip>
  )
}
